import { ThemeEvents } from '@theme/events';

const INIT_KEY = '__parcelyGateInitialized';

if (!window[INIT_KEY]) {
  window[INIT_KEY] = true;

  const state = {
    locked: true,
    container: null,
    observer: null,
    cartForm: null,
    checkoutObserver: null,
    checkoutObserverTarget: null,
    raf: null,
  };
  const metrics = window.location.search.includes('parcely_debug=1')
    ? {
        updates: 0,
        scheduledUpdates: 0,
        mutationBatches: 0,
        mutationRecords: 0,
        totalDuration: 0,
        maxDuration: 0,
        lastDuration: 0,
      }
    : null;

  if (metrics) {
    window.__parcelyGateMetrics = metrics;
  }

  const selectors = {
    lockerContainer: '[data-parcel-locker-blocks]',
    checkoutButton: '#checkout',
    ctas: '.cart__ctas',
    additionalButtons: '.additional-checkout-buttons',
    gateMessage: '[data-parcel-gate]',
    cartForm: '#cart-form',
    attributeInputs: 'input[name^="attributes["], select[name^="attributes["], textarea[name^="attributes["]',
    choiceInputs: 'input[type="radio"], input[type="checkbox"]',
  };

  const selectionNamePattern = /(parcel|parcely|locker|pickup|pick[-_ ]?up|pudo|terminal)/i;
  const cardClass = 'cart-parcely-option';
  const cardSelectedAttribute = 'data-parcely-card-selected';
  const cardTypeAttribute = 'data-parcely-card-type';
  const cardRowSelector = '.parcely_app_shipping_method';
  const countryPromptClass = 'cart-parcely-country-heading';
  const countryFieldClass = 'cart-parcely-country-field';
  const countryControlClass = 'cart-parcely-country-control';
  const countryValueClass = 'cart-parcely-country-value';
  const methodIcons = {
    locker: `
      <svg viewBox="0 0 64 64" focusable="false" aria-hidden="true">
        <path fill="#ff6b00" d="M7 12h50a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-8a3 3 0 0 1 3-3Z"></path>
        <path fill="#fff3e7" d="M9 26h46v28H9z"></path>
        <path fill="#ff6b00" d="M11 29h14v7H11zm19 0h4v25h-4zm9 0h14v7H39zM11 40h14v5H11zm28 0h14v5H39zM11 49h14v5H11zm28 0h14v5H39z"></path>
        <path fill="#3f4448" d="M27 26h10v28H27z"></path>
        <path fill="#ffffff" d="M29 32h6v8h-6z"></path>
        <path stroke="#ffffff" stroke-width="2.5" d="M29.5 47.5h5"></path>
        <path stroke="#3f4448" stroke-width="2.25" d="M9 26v28h46V26"></path>
      </svg>
    `,
    courier: `
      <svg viewBox="0 0 64 64" focusable="false" aria-hidden="true">
        <path stroke="#d40511" stroke-width="4" d="M6 26h14M4 34h18M8 42h10"></path>
        <path fill="#ffcc00" d="M18 18h28a4 4 0 0 1 4 4v8h4l6 9v9H18z"></path>
        <path fill="#d40511" d="M21 31h27v5H21zm0 8h35v4H21z"></path>
        <path fill="#fff8d6" d="M50 31h5l3 6h-8z"></path>
        <path stroke="#2e3033" stroke-width="2.5" d="M18 48V22a4 4 0 0 1 4-4h24a4 4 0 0 1 4 4v8h4l6 9v9H18z"></path>
        <circle cx="27" cy="49" r="6" fill="#2e3033"></circle>
        <circle cx="27" cy="49" r="2.4" fill="#ffffff"></circle>
        <circle cx="51" cy="49" r="6" fill="#2e3033"></circle>
        <circle cx="51" cy="49" r="2.4" fill="#ffffff"></circle>
      </svg>
    `,
  };

  const inputHasValue = (input) => {
    if (input instanceof HTMLInputElement) {
      if (input.type === 'checkbox' || input.type === 'radio') {
        return input.checked;
      }
    }
    return Boolean(input.value && input.value.trim());
  };

  const getContainer = () => document.querySelector(selectors.lockerContainer) || document.getElementById('parcel-locker');

  // Display copy is rendered from locales/*.json onto the container (sections/main-cart.liquid).
  const copy = (name) => getContainer()?.dataset[name] || '';

  const getCountryPromptText = () => copy('countryPrompt');

  const syncMetricsAttributes = (container) => {
    if (!metrics || !container) return;

    container.dataset.parcelyMetricUpdates = String(metrics.updates);
    container.dataset.parcelyMetricScheduled = String(metrics.scheduledUpdates);
    container.dataset.parcelyMetricMutationBatches = String(metrics.mutationBatches);
    container.dataset.parcelyMetricMutationRecords = String(metrics.mutationRecords);
    container.dataset.parcelyMetricTotalDuration = metrics.totalDuration.toFixed(2);
    container.dataset.parcelyMetricMaxDuration = metrics.maxDuration.toFixed(2);
    container.dataset.parcelyMetricLastDuration = metrics.lastDuration.toFixed(2);
  };

  const hasAttributeSelection = (root) => {
    if (!root) return false;
    const attributeInputs = Array.from(root.querySelectorAll(selectors.attributeInputs));
    if (attributeInputs.length > 0) {
      return attributeInputs.some(inputHasValue);
    }
    return false;
  };

  const hasNamedSelectionInput = (root) => {
    if (!root) return false;
    const inputs = Array.from(root.querySelectorAll('input, select, textarea'));
    return inputs.some((input) => {
      const identifier = `${input.name || ''} ${input.id || ''} ${input.getAttribute('aria-label') || ''} ${input.getAttribute('data-label') || ''}`;
      if (!selectionNamePattern.test(identifier)) return false;
      return inputHasValue(input);
    });
  };

  const getInputCard = (input, container) => {
    const directLabel = input.closest('label');
    if (directLabel) return directLabel;

    const parcelyRow = input.closest(cardRowSelector);
    if (parcelyRow) return parcelyRow;

    let element = input.parentElement;

    while (element && element !== container && element !== document.body) {
      if (element.tagName === 'LI') return element;

      const className = typeof element.className === 'string' ? element.className.toLowerCase() : '';
      if (className.includes('cart-parcely-option__')) {
        element = element.parentElement;
        continue;
      }
      if (/(option|method|shipping|delivery)/.test(className)) return element;

      element = element.parentElement;
    }

    return input.parentElement;
  };

  const selectCardInput = (input) => {
    if (!(input instanceof HTMLInputElement) || input.disabled) return;
    input.click();
  };

  const getCardType = (card) => {
    const methodTitle = card.querySelector('.cart-parcely-option__method-title')?.textContent || '';
    const text = `${methodTitle} ${card.dataset.parcelyMethodSource || ''} ${cleanMethodText(card.textContent || '')}`.toLowerCase();
    if (/courier|kuller|kurjer|kuriiri/.test(text)) return 'courier';
    if (/\bparcel locker\b|\blocker\b|pakiautomaa|pakomāt|paku skap|paštomat|pakettiautomaat/.test(text)) return 'locker';

    return 'option';
  };

  // Parcely's method labels per store language (EN, ET, LV, LT, FI), e.g. "Kuller: 10.90€, tarne ...".
  const methodLabels = {
    locker: ['Parcel locker', 'Pakiautomaadid', 'Pakiautomaat', 'Pakomāts', 'Paštomatas', 'Pakettiautomaatti'],
    courier: ['Courier', 'Kuller', 'Kurjers', 'Kurjeris', 'Kuriiri'],
  };

  const cleanMethodText = (text) =>
    [...methodLabels.locker, ...methodLabels.courier]
      .reduce(
        (result, label) => result.replace(new RegExp(`${label}(?=[^\\s:])`, 'g'), `${label}: `),
        text.replace(/\.[^{]+\{[^}]*\}/g, ' '),
      )
      .replace(/(business days)\./g, '$1')
      .replace(/\s+/g, ' ')
      .trim();

  // Parcel locker prices and free-shipping thresholds per delivery country are rendered from
  // theme settings onto the container as parallel "|"-separated lists (sections/main-cart.liquid).
  const getLockerRates = () => {
    const dataset = getContainer()?.dataset;
    const countryGroups = (dataset?.lockerCountries || '').split('|');
    const prices = (dataset?.lockerPrices || '').split('|');
    const freeThresholds = (dataset?.lockerFreeThresholds || '').split('|');
    const rates = {};

    countryGroups.forEach((group, index) => {
      for (const country of group.split(',')) {
        if (country) rates[country] = { price: prices[index] || '', freeThreshold: Number(freeThresholds[index] || 0) };
      }
    });

    return rates;
  };

  // The country picked in Parcely's locker selector, or the storefront language's country before that.
  const getDeliveryCountry = () => {
    const container = getContainer();
    const selected = container?.querySelector('.country_alpha')?.textContent?.trim().toUpperCase();

    return selected || container?.dataset.defaultCountry || '';
  };

  const getLockerPrice = () => {
    const container = getContainer();
    const rates = getLockerRates();
    const rate = rates[getDeliveryCountry()] || rates[container?.dataset.defaultCountry || ''];
    if (!rate) return '';

    const cartTotal = Number(container?.dataset.cartTotal || 0);
    const isFree = rate.freeThreshold > 0 && cartTotal >= rate.freeThreshold;

    return isFree ? copy('freePrice') : rate.price;
  };

  // Show the free-shipping progress bar of the delivery country (snippets/free-shipping-progress.liquid).
  const syncFreeShippingProgress = () => {
    const country = getDeliveryCountry();
    const groups = Array.from(document.querySelectorAll('.free-shipping-progress[data-countries]'));
    if (!groups.some((group) => group.dataset.countries.split(',').includes(country))) return;

    for (const group of groups) {
      group.hidden = !group.dataset.countries.split(',').includes(country);
    }
  };

  const getMethodLabelPattern = (cardType) =>
    `(?:${(cardType === 'courier' ? methodLabels.courier : methodLabels.locker).join('|')})`;

  const getMethodDetails = (card, cardType) => {
    const source = card.dataset.parcelyMethodSource || cleanMethodText(card.textContent || '');
    card.dataset.parcelyMethodSource = source;

    const title = cardType === 'courier' ? copy('courierTitle') : copy('lockerTitle');
    // The price may use a decimal comma ("2,90 €"), so match the number instead of splitting at the first comma.
    const match = source.match(
      new RegExp(`${getMethodLabelPattern(cardType)}\\s*:\\s*(\\d+(?:[.,]\\d+)?\\s*€?)[,\\s]*(.*)`, 'i'),
    );
    // Parcely's label carries one fixed price; ours depend on the delivery country, so it is only the fallback.
    const parsedPrice = (match?.[1] || '').replace(/^[:\s]+/, '').trim();
    const price = (cardType === 'locker' ? getLockerPrice() : copy('courierPrice')) || parsedPrice;
    const description =
      cardType === 'locker'
        ? copy('lockerDescription')
        : cardType === 'courier'
          ? copy('courierDescription')
          : (match?.[2] || '').replace(/^[:\s,]+/, '').trim();

    return {
      title,
      price,
      description,
    };
  };

  const getDirectChildFor = (card, element) => {
    let child = element;

    while (child && child.parentElement !== card) {
      child = child.parentElement;
    }

    return child;
  };

  const ensureMethodCopy = (card, cardType) => {
    if (cardType !== 'locker' && cardType !== 'courier') return;

    const details = getMethodDetails(card, cardType);
    const existingCopy = card.querySelector('.cart-parcely-option__method-copy');
    const methodCopy = existingCopy || document.createElement('span');
    const copySignature = `${details.title}|${details.price}|${details.description}`;

    methodCopy.className = 'cart-parcely-option__copy cart-parcely-option__method-copy';

    if (methodCopy.dataset.parcelyCopySignature !== copySignature) {
      methodCopy.replaceChildren();

      const title = document.createElement('span');
      title.className = 'cart-parcely-option__method-title';
      title.textContent = details.title;
      methodCopy.append(title);

      if (details.price) {
        const price = document.createElement('span');
        price.className = 'cart-parcely-option__method-price';
        price.textContent = details.price;
        methodCopy.append(price);
      }

      if (details.description) {
        const description = document.createElement('span');
        description.className = 'cart-parcely-option__method-description';
        description.textContent = details.description;
        methodCopy.append(description);
      }

      methodCopy.dataset.parcelyCopySignature = copySignature;
    }

    if (!existingCopy) {
      const methodIcon = card.querySelector('.cart-parcely-option__method-icon');
      if (methodIcon) {
        methodIcon.insertAdjacentElement('afterend', methodCopy);
      } else {
        card.append(methodCopy);
      }
    }

    for (const copy of Array.from(card.querySelectorAll('.cart-parcely-option__copy'))) {
      if (copy !== methodCopy) {
        copy.classList.add('cart-parcely-option__source-copy');
      }
    }
  };

  const syncCardParts = (card, input, cardType) => {
    if (!input.classList.contains('cart-parcely-option__control')) {
      input.classList.add('cart-parcely-option__control');
    }

    const controlChild = getDirectChildFor(card, input);
    if (controlChild && !controlChild.classList.contains('cart-parcely-option__control-cell')) {
      controlChild.classList.add('cart-parcely-option__control-cell');
    }

    const media = card.querySelector('.cart-parcely-option__method-icon') || card.querySelector('img, svg');
    const mediaChild = media ? getDirectChildFor(card, media) : null;

    if (
      mediaChild &&
      mediaChild !== input &&
      mediaChild !== controlChild &&
      !mediaChild.classList.contains('cart-parcely-option__visual')
    ) {
      mediaChild.classList.add('cart-parcely-option__visual');
    }

    for (const child of Array.from(card.children)) {
      if (!(child instanceof HTMLElement)) continue;
      if (child === input || child === controlChild || child === mediaChild) continue;
      if (child.matches('input, select, textarea, button')) continue;
      if (child.classList.contains('cart-parcely-option__method-icon')) continue;

      if ((cardType === 'locker' || cardType === 'courier') && child.querySelector('svg')) {
        if (!child.classList.contains('cart-parcely-option__legacy-icon')) {
          child.classList.add('cart-parcely-option__legacy-icon');
        }
        continue;
      }

      if (!child.classList.contains('cart-parcely-option__copy')) {
        child.classList.add('cart-parcely-option__copy');
      }
    }

    ensureMethodCopy(card, cardType);
  };

  const syncCardGroup = (card, cardType) => {
    const parent = card.parentElement;
    if (!parent) return;

    if (cardType === 'locker' || cardType === 'courier') {
      if (!parent.classList.contains('cart-parcely-method-grid')) {
        parent.classList.add('cart-parcely-method-grid');
      }
      return;
    }
  };

  const cleanupNestedCards = (card) => {
    const nestedCards = Array.from(card.querySelectorAll(`.${cardClass}`)).filter((nestedCard) => nestedCard !== card);

    nestedCards.forEach((nestedCard) => {
      nestedCard.classList.remove(cardClass);
      nestedCard.removeAttribute(cardSelectedAttribute);
      nestedCard.removeAttribute(cardTypeAttribute);
      nestedCard.removeAttribute('data-parcely-card-bound');
    });
  };

  const ensureMethodIcon = (card, cardType, input) => {
    if (cardType !== 'locker' && cardType !== 'courier') return;
    if (card.querySelector('.cart-parcely-option__method-icon')) return;

    const controlChild = getDirectChildFor(card, input);
    const icon = document.createElement('span');
    icon.className = 'cart-parcely-option__method-icon';
    icon.innerHTML = methodIcons[cardType];

    if (controlChild && controlChild.parentElement === card) {
      controlChild.insertAdjacentElement('afterend', icon);
      return;
    }

    input.insertAdjacentElement('afterend', icon);
  };

  const getEnhanceableInputs = (container) => {
    const inputs = [];
    const rows = container.querySelectorAll(cardRowSelector);

    for (const row of rows) {
      const input = row.querySelector(selectors.choiceInputs);
      if (input instanceof HTMLInputElement) {
        inputs.push(input);
      }
    }

    if (inputs.length) return inputs;

    const fallbackInputs = container.querySelectorAll(selectors.choiceInputs);
    if (fallbackInputs.length > 12) return [];

    return Array.from(fallbackInputs).filter((input) => input instanceof HTMLInputElement);
  };

  const hasSubscriptionCart = (container) => container?.dataset.subscriptionCart === 'true';

  const syncSubscriptionShipping = (container) => {
    if (!container) return;

    const lockerOnly = hasSubscriptionCart(container);
    const courierCards = container.querySelectorAll(`.${cardClass}[${cardTypeAttribute}='courier']`);
    const lockerCard = container.querySelector(`.${cardClass}[${cardTypeAttribute}='locker']`);
    const lockerInput = lockerCard?.querySelector(selectors.choiceInputs);

    for (const card of courierCards) {
      const input = card.querySelector(selectors.choiceInputs);
      const courierSelected = input instanceof HTMLInputElement && input.checked;

      card.hidden = lockerOnly;
      card.setAttribute('aria-hidden', lockerOnly ? 'true' : 'false');

      if (input instanceof HTMLInputElement) {
        input.disabled = lockerOnly;
        if (lockerOnly && courierSelected && lockerInput instanceof HTMLInputElement && !lockerInput.checked) {
          selectCardInput(lockerInput);
        }
      }
    }

    if (
      lockerOnly &&
      lockerInput instanceof HTMLInputElement &&
      !lockerInput.checked &&
      !lockerInput.disabled
    ) {
      selectCardInput(lockerInput);
    }
  };

  const enhanceParcelyCards = (container) => {
    if (!container) return;

    const inputs = getEnhanceableInputs(container);

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const card = getInputCard(input, container);
      if (!card) continue;

      if (!card.classList.contains(cardClass)) {
        card.classList.add(cardClass);
        cleanupNestedCards(card);
      }

      const cardType = getCardType(card);
      if (card.getAttribute(cardTypeAttribute) !== cardType) {
        card.setAttribute(cardTypeAttribute, cardType);
      }
      syncCardGroup(card, cardType);
      ensureMethodIcon(card, cardType, input);
      syncCardParts(card, input, cardType);

      const selectedValue = input.checked ? 'true' : 'false';
      if (card.getAttribute(cardSelectedAttribute) !== selectedValue) {
        card.setAttribute(cardSelectedAttribute, selectedValue);
      }

      if (card.tagName === 'LABEL' || card.dataset.parcelyCardBound === 'true') continue;

      card.dataset.parcelyCardBound = 'true';
      card.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('a, button, input, select, textarea, label')) return;
        selectCardInput(input);
      });
    }
  };

  const syncLoadingState = (container) => {
    if (!container) return;

    const hasShippingOptions = Boolean(container.querySelector(`.${cardClass}, ${cardRowSelector}`));
    if (hasShippingOptions) {
      delete container.dataset.parcelyLoading;
      container.removeAttribute('aria-busy');
      return;
    }

    container.dataset.parcelyLoading = 'true';
    container.setAttribute('aria-busy', 'true');
  };

  const ensureCountryPrompt = (container) => {
    if (!container) return;

    const existingPrompt = container.querySelector(`.${countryPromptClass}`);
    const lockerCard = container.querySelector(`.${cardClass}[${cardTypeAttribute}='locker']`);
    const lockerInput = lockerCard?.querySelector('input[type="radio"], input[type="checkbox"]');
    const isLockerSelected = lockerInput instanceof HTMLInputElement && lockerInput.checked;

    if (!isLockerSelected) {
      existingPrompt?.remove();
      container.querySelectorAll(`.${countryFieldClass}`).forEach((field) => {
        field.classList.remove(countryFieldClass);
      });
      container.querySelectorAll(`.${countryControlClass}`).forEach((control) => {
        control.classList.remove(countryControlClass);
      });
      container.querySelectorAll(`.${countryValueClass}`).forEach((value) => {
        value.classList.remove(countryValueClass);
      });
      return;
    }

    const widget = container.querySelector('[id="parcelyWidget"]') || container;
    const countryInput = widget.querySelector('input[id^="react-select-"][role="combobox"]');
    const countryControl = countryInput?.closest('[class*="control"]');

    if (
      !(countryControl instanceof HTMLElement) ||
      countryControl.closest(`.${cardClass}`)
    ) {
      existingPrompt?.remove();
      return;
    }

    const app = countryControl.closest('.parcely_app') || widget;
    const countryRow = getDirectChildFor(app, countryControl) || countryControl;
    const prompt = existingPrompt || document.createElement('div');
    const countryValue = countryControl.querySelector('[class*="singleValue"]');

    prompt.className = countryPromptClass;
    prompt.textContent = getCountryPromptText();
    countryRow.classList.add(countryFieldClass);
    countryControl.classList.add(countryControlClass);
    countryValue?.classList.add(countryValueClass);

    if (prompt.parentElement !== countryRow || countryRow.firstElementChild !== prompt) {
      countryRow.insertAdjacentElement('afterbegin', prompt);
    }
  };

  const getLockState = () => {
    const container = getContainer();
    const checkoutButton = document.querySelector(selectors.checkoutButton);

    // Parcely owns the native disabled state while a multi-step locker choice is incomplete.
    if (checkoutButton instanceof HTMLButtonElement && checkoutButton.disabled) return true;

    return !isSelectionComplete(container);
  };

  const setAttributeValue = (element, name, value) => {
    if (element.getAttribute(name) !== value) {
      element.setAttribute(name, value);
    }
  };

  const removeAttributeValue = (element, name) => {
    if (element.hasAttribute(name)) {
      element.removeAttribute(name);
    }
  };

  const isSelectionComplete = (container) => {
    if (container) {
      const choiceInputs = Array.from(container.querySelectorAll(selectors.choiceInputs));
      if (choiceInputs.some(inputHasValue)) return true;
      if (hasAttributeSelection(container)) return true;
      if (hasNamedSelectionInput(container)) return true;
    }

    const cartForm = state.cartForm || document.querySelector(selectors.cartForm);
    const hasFormSelection = cartForm && cartForm !== container ? hasNamedSelectionInput(cartForm) : false;

    return hasFormSelection;
  };

  const ensureObserver = (container) => {
    if (!container || container === state.container) return;
    state.container = container;
    attachObserver(container);
  };

  const applyLockedState = (locked) => {
    const checkoutButton = document.querySelector(selectors.checkoutButton);
    const ctas = document.querySelector(selectors.ctas);
    const additionalButtons = document.querySelector(selectors.additionalButtons);
    const gateMessage = document.querySelector(selectors.gateMessage);

    state.locked = locked;

    if (ctas) {
      const lockedValue = locked ? 'true' : 'false';
      if (ctas.dataset.parcelLocked !== lockedValue) {
        ctas.dataset.parcelLocked = lockedValue;
      }
    }

    if (checkoutButton) {
      if (locked) {
        setAttributeValue(checkoutButton, 'aria-disabled', 'true');
      } else {
        removeAttributeValue(checkoutButton, 'aria-disabled');
      }
    }

    if (gateMessage) {
      gateMessage.toggleAttribute('hidden', !locked);
    }

    if (additionalButtons) {
      if (locked) {
        setAttributeValue(additionalButtons, 'aria-hidden', 'true');
        setAttributeValue(additionalButtons, 'inert', '');
      } else {
        removeAttributeValue(additionalButtons, 'aria-hidden');
        removeAttributeValue(additionalButtons, 'inert');
      }
    }
  };

  const ensureCheckoutObserver = () => {
    const checkoutButton = document.querySelector(selectors.checkoutButton);
    if (!checkoutButton) return;
    if (state.checkoutObserverTarget === checkoutButton) return;
    if (state.checkoutObserver) {
      state.checkoutObserver.disconnect();
    }
    state.checkoutObserverTarget = checkoutButton;
    state.checkoutObserver = new MutationObserver(scheduleUpdate);
    state.checkoutObserver.observe(checkoutButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'data-disabled'],
    });
  };

  const ensureFormHandler = () => {
    const cartForm = document.querySelector(selectors.cartForm);
    if (cartForm && cartForm !== state.cartForm) {
      state.cartForm = cartForm;
      cartForm.addEventListener('submit', onSubmit);
    }
  };

  const updateState = () => {
    const startedAt = metrics ? window.performance.now() : 0;
    const container = getContainer();
    ensureObserver(container);
    ensureCheckoutObserver();
    enhanceParcelyCards(container);
    syncSubscriptionShipping(container);
    syncLoadingState(container);
    ensureCountryPrompt(container);
    syncFreeShippingProgress();
    const locked = getLockState();
    applyLockedState(locked);
    ensureFormHandler();

    if (metrics) {
      const duration = window.performance.now() - startedAt;
      metrics.updates += 1;
      metrics.lastDuration = duration;
      metrics.totalDuration += duration;
      metrics.maxDuration = Math.max(metrics.maxDuration, duration);
      syncMetricsAttributes(container);
    }
  };

  const scheduleUpdate = () => {
    if (state.raf) return;
    if (metrics) {
      metrics.scheduledUpdates += 1;
    }
    state.raf = window.requestAnimationFrame(() => {
      state.raf = null;
      updateState();
    });
  };

  const detachObserver = () => {
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }
  };

  const attachObserver = (container) => {
    if (!container) return;
    detachObserver();

    state.observer = new MutationObserver((mutations) => {
      if (metrics) {
        metrics.mutationBatches += 1;
        metrics.mutationRecords += mutations.length;
      }
      scheduleUpdate();
    });
    state.observer.observe(container, {
      childList: true,
      subtree: true,
    });

    container.addEventListener('input', scheduleUpdate);
    container.addEventListener('change', scheduleUpdate);
    container.addEventListener('click', scheduleUpdate);
  };

  const onSubmit = (event) => {
    const locked = getLockState();
    applyLockedState(locked);
    if (!locked) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const gateMessage = document.querySelector(selectors.gateMessage);
    const container = getContainer();

    gateMessage?.removeAttribute('hidden');
    container?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const bind = () => {
    ensureObserver(getContainer());

    ensureFormHandler();

    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      const items = event.detail?.resource?.items;
      const container = getContainer();
      if (container && Array.isArray(items)) {
        const lockerOnly = items.some(
          (item) => Boolean(item.selling_plan_allocation) || item.handle === 'monthly-coffee-box'
        );
        container.dataset.subscriptionCart = lockerOnly ? 'true' : 'false';
      }
      scheduleUpdate();
    });
    document.addEventListener('shopify:section:load', scheduleUpdate);
    document.addEventListener('shopify:section:reorder', scheduleUpdate);
    document.addEventListener('shopify:section:select', scheduleUpdate);

    scheduleUpdate();

    // Parcely hydrates its React controls asynchronously and can change checked properties without DOM mutations.
    for (const delay of [250, 1000, 3000]) {
      window.setTimeout(scheduleUpdate, delay);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
}
